import { Maybe, matchEquals } from '@nf/utils-common/fpw/monadic';
import { matchService } from '@/services/match.service';

enum BetBuilderStatus { 
	CLOSED = 0, 
	PREMATCH = 1, 
	MULTI_PREMATCH = 2, 
	INPLAY = 3, 
	BOTH = 4 
};

interface SmpCheckParams {
	sport: number;
	id34: string;
	isLive: boolean;
	isIR: boolean;
	betBuilderStatus: BetBuilderStatus;
	showTime: string;
	isParlayMode?: boolean;
}

interface SmpPermissions {
	smp1?: boolean;
	smp2?: boolean;
	smpLive1?: boolean;
	smpLive2?: boolean;
}

const checkBetBuilderStatus = (isLive: boolean, betBuilderStatus: BetBuilderStatus): boolean => {
	return matchEquals(betBuilderStatus)
		.on(BetBuilderStatus.CLOSED, () => false)
		.on(BetBuilderStatus.PREMATCH, () => !isLive)
		.on(BetBuilderStatus.MULTI_PREMATCH, () => !isLive)
		.on(BetBuilderStatus.INPLAY, () => isLive)
		.on(BetBuilderStatus.BOTH, () => true)
		.otherwise(() => false);
};

const checkSmpRules = (
	sport: number, 
	liveTimerMinutes: number, 
	isIR: boolean
) => {
	if (sport !== 1) {
		return true;
	}

	return liveTimerMinutes < 45 && !isIR;
};

const checkSmpPermissions = (
	sport: number,
	isLive: boolean,
	smpPermissions: SmpPermissions | null | undefined
): boolean => {
	return Maybe(smpPermissions).fold(
		() => false,
		(permissions: SmpPermissions) => 
			matchEquals(sport)
				.on(1, () => 
					isLive ? Boolean(permissions.smpLive1) : Boolean(permissions.smp1)
				)
				.on(2, () =>
					isLive ? Boolean(permissions.smpLive2) : Boolean(permissions.smp2)
				)
				.otherwise(() => false)
	);
};

const checkSmpAvailability = (
	params: SmpCheckParams,
	smpPermissions: SmpPermissions | null | undefined
): boolean => {
	const {
		sport,
		id34,
		isLive,
		isIR,
		betBuilderStatus,
		showTime,
		isParlayMode = false,
	} = params;

	if (isParlayMode || !id34) {
		return false;
	}

	const liveTimerMinutes = matchService.extractLiveTimerMinutes(showTime);
	const isSmpAvailable = checkSmpRules(sport, liveTimerMinutes, isIR);
	const hasPermission = checkSmpPermissions(sport, isLive, smpPermissions);

	return matchEquals(sport)
		.on(1, () => {
			const canUseSingleMatchParlay = checkBetBuilderStatus(isLive, betBuilderStatus);
			return isSmpAvailable && hasPermission && canUseSingleMatchParlay;
		})
		.on(2, () => {
			return isSmpAvailable && hasPermission && !isLive
		})
		.otherwise(() => false);
};

export const smpService = {
	checkSmpAvailability
};